/**
 * Safe Logger Utility
 * 
 * Provides environment-aware logging that:
 * - In development: Full console output for debugging
 * - In production: Only warn/error with sanitized payloads (no PII)
 * 
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.debug('Debug message', { data });
 *   logger.info('Info message');
 *   logger.warn('Warning', { context });
 *   logger.error('Error occurred', error);
 */

const isProduction = process.env.NODE_ENV === 'production';

/**
 * Keys that contain sensitive data and should be redacted
 */
const SENSITIVE_KEYS = [
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'resetToken',
  'apiKey',
  'secret',
  'authorization',
  'cookie',
  'email',
  'phone',
  'address',
  'street',
  'postcode',
  'postalCode',
  'zipCode',
  'city',
  'firstName',
  'lastName',
  'name',
  'creditCard',
  'cardNumber',
  'cvv',
  'expiry',
  'iban',
  'bic',
  'ssn',
  'socialSecurity',
];

/**
 * Check if a key is sensitive
 */
const isSensitiveKey = (key) => {
  if (!key || typeof key !== 'string') return false;
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYS.some(sensitive => 
    lowerKey.includes(sensitive.toLowerCase())
  );
};

/**
 * Sanitize an object by redacting sensitive values
 * @param {any} obj - Object to sanitize
 * @param {number} depth - Current recursion depth
 * @returns {any} Sanitized copy
 */
const sanitize = (obj, depth = 0) => {
  // Prevent infinite recursion
  if (depth > 5) return '[MAX_DEPTH]';
  
  // Handle null/undefined
  if (obj === null || obj === undefined) return obj;
  
  // Handle primitives
  if (typeof obj !== 'object') return obj;
  
  // Handle Error objects
  if (obj instanceof Error) {
    return {
      name: obj.name,
      message: obj.message,
      // Don't include stack in production
      ...(isProduction ? {} : { stack: obj.stack })
    };
  }
  
  // Handle arrays
  if (Array.isArray(obj)) {
    // In production, limit array output
    if (isProduction && obj.length > 10) {
      return `[Array(${obj.length}) - truncated]`;
    }
    return obj.map(item => sanitize(item, depth + 1));
  }
  
  // Handle objects
  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    if (isSensitiveKey(key)) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitize(value, depth + 1);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Format arguments for logging
 */
const formatArgs = (args, shouldSanitize = false) => {
  if (!shouldSanitize) return args;
  
  return args.map(arg => {
    if (typeof arg === 'object') {
      return sanitize(arg);
    }
    return arg;
  });
};

/**
 * Logger object with environment-aware methods
 */
export const logger = {
  /**
   * Debug level - development only
   */
  debug: (...args) => {
    if (!isProduction) {
      console.log('[DEBUG]', ...args);
    }
    // No-op in production
  },

  /**
   * Info level - development only
   */
  info: (...args) => {
    if (!isProduction) {
      console.info('[INFO]', ...args);
    }
    // No-op in production
  },

  /**
   * Warning level - always logged but sanitized in production
   */
  warn: (...args) => {
    if (isProduction) {
      console.warn('[WARN]', ...formatArgs(args, true));
    } else {
      console.warn('[WARN]', ...args);
    }
  },

  /**
   * Error level - always logged but sanitized in production
   */
  error: (...args) => {
    if (isProduction) {
      console.error('[ERROR]', ...formatArgs(args, true));
    } else {
      console.error('[ERROR]', ...args);
    }
  },

  /**
   * Group logging (development only)
   */
  group: (label) => {
    if (!isProduction) {
      console.group(label);
    }
  },

  /**
   * End group (development only)
   */
  groupEnd: () => {
    if (!isProduction) {
      console.groupEnd();
    }
  },

  /**
   * Table logging (development only)
   */
  table: (data) => {
    if (!isProduction) {
      console.table(data);
    }
  }
};

export default logger;
